import React, { memo, useCallback } from 'react';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { AiFormFieldProvenanceSource } from 'constants/aiProcessRuns';

import { FORM_REVIEW_LABELS, FormReviewTestIds } from '../FormReviewCard.constants';
import { docLinkStyles, fieldMetaRowStyles } from '../FormReviewCard.styles';

const PROVENANCE_SOURCE_LABELS = {
  [AiFormFieldProvenanceSource.AI_EXTRACTED]: 'AI extracted',
  [AiFormFieldProvenanceSource.DATA_SOURCE]: 'Data source',
  [AiFormFieldProvenanceSource.MANUAL]: 'Manual',
};

const buildDocReference = field => {
  if (!field.provenanceDocumentId) {
    return null;
  }

  const pageSuffix = field.provenancePage ? ` · p.${field.provenancePage}` : '';

  return `Document${pageSuffix}`;
};

// Provenance is rendered defensively: a Manual / DataSource field may carry no
// document at all, in which case we only show the source (and any pointer). When
// an AI-extracted field references a document we surface a deep-link the reader
// can open in the full Document view; the doc overlay itself stays out of this
// compact cell.
const FormFieldProvenance = ({ field, onOpenDocument }) => {
  const onDocLinkClick = useCallback(() => onOpenDocument?.(field), [field, onOpenDocument]);

  if (!field?.provenanceSource) {
    return null;
  }

  const sourceLabel = PROVENANCE_SOURCE_LABELS[field.provenanceSource] ?? field.provenanceSource;
  const hasDocLink =
    field.provenanceSource === AiFormFieldProvenanceSource.AI_EXTRACTED &&
    !!field.provenanceDocumentId;
  const docReference = buildDocReference(field);

  return (
    <Box data-testid={FormReviewTestIds.PROVENANCE_BADGE} sx={fieldMetaRowStyles}>
      <Typography component="span" variant="bold12">
        {`${FORM_REVIEW_LABELS.PROVENANCE}: `}
      </Typography>
      <Typography component="span" variant="regular12">
        {sourceLabel}
      </Typography>
      {hasDocLink && docReference ? (
        <Typography
          color="primary.main"
          component="span"
          role="link"
          sx={docLinkStyles}
          variant="regular12"
          onClick={onDocLinkClick}
        >
          {docReference}
        </Typography>
      ) : null}
      {field.provenanceDataSourcePointer ? (
        <Typography component="span" variant="regular12">
          {field.provenanceDataSourcePointer}
        </Typography>
      ) : null}
    </Box>
  );
};

FormFieldProvenance.propTypes = {
  field: PropTypes.shape({
    provenanceSource: PropTypes.string,
    provenanceDocumentId: PropTypes.string,
    provenancePage: PropTypes.number,
    provenanceRegion: PropTypes.object,
    provenanceDataSourcePointer: PropTypes.string,
  }),
  onOpenDocument: PropTypes.func,
};

export default memo(FormFieldProvenance);
